import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, MessageCircle } from "lucide-react";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Detect scroll for glassmorphism effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/services", label: "Services" },
    { path: "/brazil", label: "Why Brazil" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Logo selection based on scroll state
  const getCurrentLogo = () => {
    if (isScrolled) {
      return "./images/LOGO BLACK.png"; // Logo preta para fundo claro
    }
    return "./images/LOGO TRASPARENT BACKGOUND.png"; // Logo transparente para fundo escuro
  };

  return (
    <nav className={`elite-nav ${isScrolled ? 'scrolled' : ''}`}>
      <div className="elite-container">
        <div className="flex justify-between items-center h-28">
          {/* Logo Adaptativa Elite */}
          <Link to="/" className="flex items-center space-x-4">
            <img 
              src={getCurrentLogo()}
              alt="UNIÃO LAR & VIDA - Family-Owned Consultancy" 
              className={`h-20 w-20 object-contain logo-adaptive ${isScrolled ? 'scrolled' : ''}`}
            />
            <div className={`font-luxury text-2xl font-bold transition-colors duration-500 ${
              isScrolled ? 'text-primary' : 'text-white text-shadow-strong'
            }`}>
              UNIÃO LAR & VIDA
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-12">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-semibold transition-all duration-300 ${
                  isActive(item.path)
                    ? (isScrolled ? "text-accent border-b-2 border-accent pb-1" : "text-accent border-b-2 border-accent pb-1")
                    : (isScrolled ? "text-slate-700 hover:text-accent" : "text-white/90 hover:text-accent text-shadow-strong")
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Button asChild className={`elite-button transition-all duration-500 ${
              isScrolled 
                ? "bg-accent hover:bg-accent/90" 
                : "bg-accent/90 hover:bg-accent shadow-2xl"
            }`}>
              <Link to="/contact">
                <MessageCircle className="mr-2 h-4 w-4" />
                Private Consultation
              </Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(!isOpen)}
              className={`transition-colors duration-300 ${
                isScrolled ? 'text-primary hover:bg-slate-100' : 'text-white hover:bg-white/10'
              }`}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden border-t border-white/20 bg-white/95 backdrop-blur-md rounded-b-lg">
            <div className="py-6 space-y-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block text-base font-semibold transition-colors px-4 py-2 rounded-lg ${
                    isActive(item.path)
                      ? "text-accent bg-accent/10"
                      : "text-slate-700 hover:text-accent hover:bg-slate-50"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              <div className="pt-4 px-4">
                <Button asChild className="elite-button w-full">
                  <Link to="/contact" onClick={() => setIsOpen(false)}>
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Private Consultation
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;